import BaseModel from "lib/model";
import BaseCollection from "lib/collection";
import ItemModel from "models/item";
import CompanyModel from "models/company";
import OrderModel from "models/order";
import LineItem from "models/order/line-item";

const models = {};
models.product = ItemModel;
models.company = CompanyModel;
models.order = OrderModel;
models.line_items = LineItem;

const transformTypes = {
    string: "string",
    "long string": "string",
    number: "number",
    money: "currency",
    boolean: "boolean",
    date: "utcDate",
    image: "string",
    address: "string",
    lookup: "string",
    formula: "string",
};

const FieldDetails = BaseModel.extend({
    props: {
        type: {
            type: "string",
            values: ["single", "multi"],
            required: true,
            default: "single",
        },
        // Formula is derived (not supported), money is a number, address isnt supported,
        // Lookup only applies to orders (dont worry for now)
        subtype: {
            type: "string",
            values: [
                "string",
                "long string",
                "number",
                "money",
                "boolean",
                "date",
                "image",
                "address",
                "lookup",
                "formula",
            ],
            required: true,
            default: "string",
        },
        default_value: {
            type: "string",
            required: false,
        },

        // Company.foo for orders
        lookup: {
            type: "string",
            required: false,
        },
        validators: "array",
        formatters: "array",

        // Category subcategory fun
        value_dependency: {
            type: "string",
            required: false,
        },
        allowed_values: "array",
    },
});

const FieldModel = BaseModel.extend({
    props: {
        _id: {
            type: "string",
            required: true,
        },
        key: {
            // Can be foo.bar (one level deep) Product.sizes.foo Companies.userConnections.foo Orders.lineitems.foo
            type: "string",
            required: true,
        },
        name: {
            type: "string",
            required: true,
        },
        required: {
            type: "boolean",
            required: true,
            default: false,
        },
        no_default: {
            type: "boolean",
            required: true,
            default: false,
        },
        searchable: {
            type: "boolean",
            required: true,
            default: false,
        },
        filterable: {
            type: "boolean",
            required: true,
            default: false,
        },
        display_to_buyers: {
            type: "boolean",
            required: true,
            default: false,
        },
        display: {
            type: "boolean",
            required: true,
            default: false,
        },
    },
    children: {
        details: FieldDetails,
    },
});

const FieldCollection = BaseCollection.extend({
    indexes: ["key"],
    model: FieldModel,
});

export default BaseModel.extend({
    props: {
        _id: {
            type: "objectId",
            required: true,
        },
        type: {
            type: "string",
            required: true,
        },
        name: {
            type: "string",
            required: true,
        },
        active: {
            type: "boolean",
            required: true,
            default: false,
        },
        composite_key_fields: {
            type: "array",
        },
        created_on: {
            type: "date",
            required: true,
        },
        modified_on: {
            type: "date",
            required: true,
        },
    },

    collections: {
        fields: FieldCollection,
    },

    getModel(submodel) {
        let key = "lib/model";
        let model = models[this.type];
        if (submodel) {
            key = `Model-${submodel}`;
            model = models[submodel];
        }
        if (!this[key]) {
            this[key] = this.createSchemaModel(this, model, submodel);
        }
        return this[key];
    },

    getAttributeType(field) {
        return field.details.type === "multi" ? "array" : transformTypes[field.details.subtype];
    },

    createSchemaModel(schema, Model = BaseModel, submodel) {
        const props = {},
            session = {},
            derived = {},
            children = {},
            collections = {};

        schema.fields.forEach((field) => {
            const keys = field.key.split(".");
            const prop = {
                type: this.getAttributeType(field),
                required: field.required,
            };
            if (field.details.allowed_values && field.details.allowed_values.length && field.details.type !== "multi") {
                prop.values = field.details.allowed_values.map((val) => {
                    return val.value;
                });
            }

            if (keys.length === 1 && !submodel) {
                props[field.key] = prop;
            } else if (submodel && keys[0] === submodel) {
                props[keys[1]] = prop;
            }
        });

        return Model.extend({ props, session, derived, children, collections });
    },
});
